import React, { useState, useEffect } from "react";
import { useTrialData, useBatchUpdateOpnBal } from "http-hooks/opnbal";
import useAuthStore from "store/auth";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import Select from "components/ui/Select";
import Input from "components/ui/Input";
import Modal from "components/ui/modals";
import OutstandingDocumentsModal from "components/opnbal/OutstandingDocumentsModal";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingRow,
  EmptyRow,
  ActionButton,
} from "components/ui/Table";

// Constants moved to top to prevent re-renders
const trialTypeOptions = [
  { value: "V", label: "Vendorwise" },
  { value: "C", label: "Customerwise" },
];

export default function OpeningBalances() {
  const { agttp } = useAuthStore(); // Get user's account type
  const [selectedTrialType, setSelectedTrialType] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [tableData, setTableData] = useState([]);
  
  // Outstanding documents modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState({
    vendorId: null,
    customerId: null,
    vendorName: null,
    customerName: null
  });

  // For agttp=1 (trader), we load data directly when type is selected
  // For agttp=0 (agent), we load data when both type and account are selected
  const {
    data: trialData,
    isLoading: isTrialDataLoading,
    error: trialDataError,
  } = useTrialData(
    selectedTrialType?.value,
    agttp === 0 ? selectedAccount?.ps_Id : null
  );

  const { mutate: saveChanges, isPending: isSaving } = useBatchUpdateOpnBal({
    onSuccess: () => {
      // No longer need to set hasChanges
    },
  });

  // Update table data when trial data changes
  useEffect(() => {
    if (trialData) {
      // Ensure debit and credit are numbers
      const processedData = trialData.map(row => ({
        ...row,
        debit: parseFloat(row.debit) || 0,
        credit: parseFloat(row.credit) || 0,
      }));
      setTableData(processedData);
    }
  }, [trialData]);

  const handleTrialTypeChange = (option) => {
    setSelectedTrialType(option);
    setSelectedAccount(null);
    setTableData([]);
  };

  const handleAccountChange = (account) => {
    setSelectedAccount(account);
    setTableData([]);
  };

  const handleValueChange = (index, field, value) => {
    const numericValue = parseFloat(value) || 0;
    setTableData((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        [field]: numericValue,
      };
      return newData;
    });
  };

  const handleManageDocuments = (row) => {
    // Determine vendor and customer IDs based on current perspective
    let vendorId, customerId, vendorName, customerName;
    
    if (selectedTrialType?.value === 'V') {
      // Vendor-wise: selectedAccount is vendor, row is customer
      vendorId = selectedAccount?.ps_Id;
      customerId = row.ps_Id;
      vendorName = selectedAccount?.name;
      customerName = row.name;
    } else {
      // Customer-wise: selectedAccount is customer, row is vendor
      vendorId = row.ps_Id;
      customerId = selectedAccount?.ps_Id;
      vendorName = row.name;
      customerName = selectedAccount?.name;
    }

    // For trader (agttp=1), we don't have selectedAccount, so use different logic
    if (agttp === 1) {
      if (selectedTrialType?.value === 'V') {
        // Vendor-wise for trader: need to handle differently
        // This case might need additional logic
        vendorId = row.ps_Id;
        customerId = null; // Would need to be determined
        vendorName = row.name;
        customerName = null;
      } else {
        // Customer-wise for trader
        vendorId = null; // Would need to be determined
        customerId = row.ps_Id;
        vendorName = null;
        customerName = row.name;
      }
    }

    setModalContext({
      vendorId,
      customerId,
      vendorName,
      customerName
    });
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (!selectedTrialType || !tableData.length) {
      return;
    }

    // For agttp=0 (agent), we need selectedAccount
    if (agttp === 0 && !selectedAccount) {
      return;
    }

    // Map all records to correct structure - no filtering
    const records = tableData.map((row) => ({
      code: row.ps_Id, // This is psid in PHP
      opn_debit: row.debit,
      opn_credit: row.credit,
      actyp: "",
    }));

    const payload = {
      type: selectedTrialType.value, // Add type parameter for swap logic
      records: records,
    };

    // For agttp=0 (agent), include mainacc (selected account)
    if (agttp === 0) {
      payload.mainacc = selectedAccount.ps_Id;
    }

    saveChanges(payload, {
      onSuccess: () => {
        // No longer need to set hasChanges
      },
    });
  };

  const calculateTotals = () => {
    if (!tableData || !Array.isArray(tableData)) {
      return { totalDebit: 0, totalCredit: 0, balance: 0, balanceType: 'Debit' };
    }
    
    const totalDebit = tableData.reduce((sum, row) => {
      const debit = parseFloat(row.debit) || 0;
      return sum + debit;
    }, 0);
    
    const totalCredit = tableData.reduce((sum, row) => {
      const credit = parseFloat(row.credit) || 0;
      return sum + credit;
    }, 0);
    
    // Calculate balance (matching PHP logic)
    const balance = totalDebit - totalCredit;
    const balanceType = balance <= 0 ? 'Debit' : 'Credit';
    
    return { 
      totalDebit, 
      totalCredit, 
      balance: Math.abs(balance),
      balanceType 
    };
  };

  const { totalDebit, totalCredit, balance, balanceType } = calculateTotals();

  const getPartyLabel = () => {
    if (selectedTrialType?.value === "V") {
      return "Creditor's A/C";
    } else if (selectedTrialType?.value === "C") {
      return "Debtor's A/C";
    }
    return "Select Account";
  };

  const getPageTitle = () => {
    if (selectedTrialType?.value === "V") {
      return "Sundry Creditor's Opening Balances";
    } else if (selectedTrialType?.value === "C") {
      return "Sundry Debtor's Opening Balances";
    }
    return "Opening Balances";
  };

  const shouldShowTable = () => {
    if (agttp === 1) {
      // Trader: Show table when type is selected
      return selectedTrialType;
    } else {
      // Agent: Show table when both type and account are selected
      return selectedTrialType && selectedAccount;
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="font-semibold leading-6 text-3xl text-gray-900">
            {getPageTitle()}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage opening balances for vendors and customers
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="block rounded-md bg-blue-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Selection Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <Select
            options={trialTypeOptions}
            value={selectedTrialType}
            onChange={handleTrialTypeChange}
            label="Opening Trial Type"
            placeholder="Select trial type"
            isRequired
          />
        </div>

        {/* Account Selection - Only for agttp=0 (agent) */}
        {agttp === 0 && selectedTrialType && (
          <div>
            {selectedTrialType.value === "V" ? (
              <SelectVendors
                value={selectedAccount}
                onChange={handleAccountChange}
                label={getPartyLabel()}
                placeholder="Select vendor"
                isRequired
              />
            ) : (
              <SelectCustomers
                value={selectedAccount}
                onChange={handleAccountChange}
                label={getPartyLabel()}
                placeholder="Select customer"
                isRequired
              />
            )}
          </div>
        )}
      </div>

      {/* Balance Display - Show when data is loaded */}
      {shouldShowTable() && tableData.length > 0 && (
        <div className="mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold leading-6 text-gray-900">
                  {agttp === 0 
                    ? `Opening Balances for ${selectedAccount?.name}`
                    : `Opening Balances - ${selectedTrialType?.label}`
                  }
                </h3>
              </div>
              <div>
                <p className="text-lg font-bold text-blue-800">
                  {balanceType} Balance: ₹{balance.toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trial Data Table */}
      {shouldShowTable() && (
        <div>
          <Table>
            <THead>
              <TR>
                <TH first>S.No</TH>
                <TH>Account</TH>
                <TH>Debit</TH>
                <TH>Credit</TH>
                <TH last>Actions</TH>
              </TR>
            </THead>
            <TBody>
              {isTrialDataLoading ? (
                <LoadingRow colSpan={5} message="Loading trial data..." />
              ) : trialDataError ? (
                <EmptyRow colSpan={5} message="Error loading trial data" />
              ) : tableData?.length === 0 ? (
                <EmptyRow colSpan={5} message="No accounts found" />
              ) : (
                <>
                  {tableData?.map((row, index) => (
                    <TR key={row.ps_Id}>
                      <TD first>{row.sno}</TD>
                      <TD>
                        <div className="font-medium">{row.name}</div>
                      </TD>
                      <TD>
                        <Input
                          type="number"
                          step="0.01"
                          value={row.debit || 0}
                          onChange={(e) => handleValueChange(index, "debit", e.target.value)}
                          className="text-right"
                          min="0"
                          showLabel={false}
                        />
                      </TD>
                      <TD>
                        <Input
                          type="number"
                          step="0.01"
                          value={row.credit || 0}
                          onChange={(e) => handleValueChange(index, "credit", e.target.value)}
                          className="text-right"
                          min="0"
                          showLabel={false}
                        />
                      </TD>
                      <TD last>
                        <ActionButton
                          onClick={() => handleManageDocuments(row)}
                          variant="secondary"
                          title="Manage Documents"
                          className="mr-0"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                        </ActionButton>
                      </TD>
                    </TR>
                  ))}
                  
                  {/* Totals Row */}
                  <TR className="bg-gray-50 font-semibold">
                    <TD first></TD>
                    <TD>
                      <div className="font-semibold">Total</div>
                    </TD>
                    <TD>
                      <div className="text-right font-semibold">
                        ₹{totalDebit.toFixed(2)}
                      </div>
                    </TD>
                    <TD>
                      <div className="text-right font-semibold">
                        ₹{totalCredit.toFixed(2)}
                      </div>
                    </TD>
                    <TD last></TD>
                  </TR>
                </>
              )}
            </TBody>
          </Table>
        </div>
      )}

      {/* Instructions */}
      {!selectedTrialType && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700">
            Please select an opening trial type to begin managing opening balances.
          </p>
        </div>
      )}

      {agttp === 0 && selectedTrialType && !selectedAccount && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700">
            Please select a {selectedTrialType.label === "Vendorwise" ? "vendor" : "customer"} to view their opening balances.
          </p>
        </div>
      )}

      {/* Outstanding Documents Modal */}
      {isModalOpen && (
        <Modal open={isModalOpen} setOpen={(val) => setIsModalOpen(val)} maxWidth="6xl">
          <OutstandingDocumentsModal
            onClose={() => setIsModalOpen(false)}
            vendorId={modalContext.vendorId}
            customerId={modalContext.customerId}
            vendorName={modalContext.vendorName}
            customerName={modalContext.customerName}
          />
        </Modal>
      )}
    </div>
  );
} 